'use client';

import { Check, ChevronDown, ChevronUp, X } from 'lucide-react';
import { useState } from 'react';
import pricingData from './pricing-data.json';

// Generate comparison map from pricing data JSON
const generateComparisonMap = () => {
  // Get the plans we want to show in the comparison (excluding free_user)
  const comparisonPlans = pricingData.plans.filter(
    plan => plan.publicly_available && plan.slug !== 'free_user'
  );

  const planSlugs = comparisonPlans.map(plan => plan.slug);

  const comparisonMap: { [key: string]: boolean[] } = {};

  // Process each feature
  pricingData.features.forEach(feature => {
    if (feature.publicly_available) {
      // Check which plans include this feature
      const featureAvailability = planSlugs.map(planSlug =>
        feature.plan_slugs.includes(planSlug)
      );

      comparisonMap[feature.name] = featureAvailability;
    }
  });

  return comparisonMap;
};

export const detailedComparisonMap = generateComparisonMap();

const Row = ({ feature, values }: { feature: string; values: boolean[] }) => {
  return (
    <div className="grid grid-cols-4 px-4 py-5 text-sm text-foreground border-b border-border gap-x-16">
      <div className="text-muted-foreground">{feature}</div>
      {values.map((value: boolean, index: number) =>
        value ? (
          <div
            key={`${feature}-${index}`}
            className="flex justify-center items-center"
          >
            <Check className="w-6 h-6 text-green-500 dark:text-green-400" />
          </div>
        ) : (
          <div
            key={`${feature}-${index}`}
            className="flex justify-center items-center"
          >
            <X className="w-6 h-6 text-red-500 dark:text-red-400" />
          </div>
        )
      )}
    </div>
  );
};

const PricingFeatureTable = () => {
  const [showDetailedComparison, setShowDetailedComparison] = useState(false);

  const head = (
    <div className="grid grid-cols-4 p-4 text-sm font-medium text-foreground bg-muted border-t border-b border-border gap-x-16">
      {/* First div is intentionally empty */}
      <div></div>
      {pricingData.plans.map(
        plan =>
          plan.publicly_available && (
            <div key={plan.slug} className="flex justify-center">
              {plan.name}
            </div>
          )
      )}
    </div>
  );

  const rows = (
    <>
      {Object.entries(detailedComparisonMap).map(([feature, values]) => (
        <Row key={feature} feature={feature} values={values as boolean[]} />
      ))}
    </>
  );

  const table = (
    <div
      id="detailed-pricing"
      className={`${
        showDetailedComparison ? 'block' : 'hidden'
      } overflow-x-auto mt-8 w-full`}
    >
      <div className="overflow-hidden min-w-max">
        {head}
        {rows}
      </div>
    </div>
  );

  const trigger = (
    <div className="mt-8">
      <button
        type="button"
        className="flex items-center justify-center mx-auto font-medium text-primary hover:opacity-90"
        onClick={() => setShowDetailedComparison(!showDetailedComparison)}
      >
        Show Detailed Plan Comparison{' '}
        {showDetailedComparison ? (
          <ChevronUp className="w-5 h-5 ml-1" />
        ) : (
          <ChevronDown className="w-5 h-5 ml-1" />
        )}
      </button>
    </div>
  );

  return (
    <>
      {trigger}
      {table}
    </>
  );
};

export default PricingFeatureTable;
